import React from 'react';
import { GlobalReport, UserProfile } from '../data/mockData';
import { 
  ThumbsUp, MessageCircle, MapPin, Share2, MoreHorizontal, ShieldCheck, 
  Zap, Fuel, ShoppingBag, CloudSun, Calendar, Train, Clock, AlertTriangle, BadgeCheck, BusFront, Sparkles, UserPlus, UserCheck, ShieldAlert, Search, Droplet, HelpCircle, Repeat2
} from 'lucide-react';
import { motion } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { toast } from 'sonner';
import { copyToClipboard } from '../utils/clipboard';
import { UserPostBadge } from './UserPostBadge';
import { getDisplayName, generateAnonymousInitials } from '../utils/privacy-utils';

interface ReportCardProps {
  report: GlobalReport;
  onLike?: (id: string) => void;
  onComment?: (id: string) => void;
  onGift?: (id: string) => void;
  onUserClick?: (userName: string) => void;
  onRepost?: (id: string) => void;
  isGiftingEnabled?: boolean;
  currentUserName?: string;
  currentUserId?: string;
  isAdminDeleteMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  traffic: <MapPin className="w-4 h-4 text-red-500" />,
  power: <Zap className="w-4 h-4 text-amber-500" />,
  fuel: <Fuel className="w-4 h-4 text-blue-500" />,
  market: <ShoppingBag className="w-4 h-4 text-green-500" />,
  weather: <CloudSun className="w-4 h-4 text-sky-500" />,
  event: <Calendar className="w-4 h-4 text-purple-500" />,
  transport: <Train className="w-4 h-4 text-indigo-500" />,
  security: <ShieldCheck className="w-4 h-4 text-slate-500" />,
  nuisance: <AlertTriangle className="w-4 h-4 text-orange-500" />,
  bus: <BusFront className="w-4 h-4 text-blue-600" />,
  activity: <Sparkles className="w-4 h-4 text-purple-500" />,
  crime: <ShieldAlert className="w-4 h-4 text-red-700" />,
  find: <Search className="w-4 h-4 text-blue-500" />,
  water: <Droplet className="w-4 h-4 text-cyan-500" />,
  help: <HelpCircle className="w-4 h-4 text-amber-500" />,
};

const COUNTRY_FLAGS: Record<string, string> = {
  Nigeria: '🇳🇬',
  Ghana: '🇬🇭',
  Kenya: '🇰🇪',
  USA: '🇺🇸',
  UK: '🇬🇧',
};

export const ReportCard = React.forwardRef<HTMLDivElement, ReportCardProps>(({ 
  report, 
  onLike, 
  onComment, 
  onGift, 
  onUserClick,
  onRepost,
  isGiftingEnabled = false,
  currentUserName,
  currentUserId,
  isAdminDeleteMode = false,
  isSelected = false,
  onToggleSelect
}, ref) => {
  const isUserPost = currentUserName && report.userName === currentUserName;
  const [isFollowing, setIsFollowing] = React.useState(false);
  const [hasReposted, setHasReposted] = React.useState(false);
  
  // Get privacy-friendly display name
  const displayName = React.useMemo(() => {
    const name = getDisplayName(report.userId, report.userName, isUserPost);
    return name;
  }, [report.userId, report.userName, isUserPost]);
  
  const displayInitials = React.useMemo(() => {
    return isUserPost ? 'Y' : generateAnonymousInitials(report.userId);
  }, [report.userId, isUserPost]);
  
  const handleFollowClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    setIsFollowing(!isFollowing);
    toast.success(
      isFollowing ? 'Unfollowed' : 'Now Following!', 
      { 
        description: isFollowing 
          ? `You unfollowed ${displayName}` 
          : `You'll get updates when ${displayName} posts`,
        icon: isFollowing ? '👋' : '🔔'
      }
    );
  };
  
  // Get original author display name for reposts
  const originalDisplayName = React.useMemo(() => {
    if (report.isRepost && report.originalAuthor) {
      return getDisplayName(report.originalAuthor.userId, report.originalAuthor.userName, false);
    }
    return null;
  }, [report.isRepost, report.originalAuthor]);

  return (
    <UserPostBadge isUserPost={!!isUserPost}>
      <motion.div 
        ref={ref}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative bg-white border-2 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all ${
          isAdminDeleteMode && isSelected 
            ? 'border-red-500 bg-red-50/30' 
            : isAdminDeleteMode 
            ? 'border-slate-300 hover:border-red-300' 
            : 'border-slate-200 hover:border-green-300'
        }`}
      >
      {/* Repost Banner */}
      {report.isRepost && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100 px-4 py-2 flex items-center gap-2">
          <Repeat2 className="w-4 h-4 text-green-600" />
          <span className="text-xs font-semibold text-green-700">
            {displayName} reposted from {originalDisplayName}
          </span>
        </div>
      )}

      {/* Admin Delete Mode Checkbox */}
      {isAdminDeleteMode && (
        <div className="absolute top-3 left-3 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect?.(report.id)}
            className="w-5 h-5 rounded border-2 border-red-400 text-red-600 focus:ring-2 focus:ring-red-500 cursor-pointer"
          />
        </div>
      )}
      
      {/* Repost Note (if user added commentary) */}
      {report.isRepost && report.repostNote && (
        <div className="px-4 pt-4 pb-2">
          <p className="text-slate-700 text-sm font-medium italic">
            "{report.repostNote}"
          </p>
        </div>
      )}
      
      {/* Header */}
      <div className={`p-4 flex items-start justify-between ${report.isRepost && report.repostNote ? 'pt-2' : ''}`}>
        {/* IMPROVEMENT 1: Prominent Location Badge */}
        <div className="absolute top-3 right-3 bg-emerald-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg z-10 hover:bg-emerald-700 transition-colors">
          <MapPin className="w-3.5 h-3.5" />
          <span>{report.location || report.lga || 'Unknown'}, {report.state || report.region || ''}</span>
        </div>
        
        <div className={`flex gap-3 flex-1 ${isAdminDeleteMode ? 'ml-8' : ''}`}>
          <div 
            onClick={() => onUserClick?.(report.userName)}
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 hover:border-green-400 transition-all cursor-pointer"
          >
            {report.userAvatar ? (
              <img src={report.userAvatar} alt={report.userName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-slate-400 font-medium">{displayInitials}</span>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                onClick={() => onUserClick?.(report.userName)}
                className="font-semibold text-slate-900 hover:text-green-600 transition-colors cursor-pointer"
              >
                {displayName}
              </span>
              {report.userTrustScore > 1000 && (
                <BadgeCheck className="w-4 h-4 text-blue-500 fill-blue-500/10" />
              )}
              {report.metadata?.isOfficial && (
                <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Official</span>
              )}
              {/* Follow button - visible and prominent */}
              {!isUserPost && (
                <button
                  onClick={handleFollowClick}
                  className={`ml-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all active:scale-95 flex items-center gap-1 ${
                    isFollowing
                      ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                      : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                  }`}
                  title={isFollowing ? 'Click to unfollow' : 'Follow for updates'}
                >
                  {isFollowing ? (
                    <>
                      <UserCheck className="w-3 h-3" />
                      Following
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-3 h-3" />
                      Follow
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                {COUNTRY_FLAGS[report.country]} {report.country}
              </span>
              <span>•</span>
              <span>{report.timestamp}</span>
              {report.syncing && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-blue-500 animate-pulse">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Syncing
                  </span>
                </>
              )}
              {report.id && report.id.startsWith('local-') && !report.syncing && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-amber-500" title="Saved locally, will sync when online">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                    Local
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => {
              if (!isGiftingEnabled) {
                toast.warning('Gifting Suspended', { description: 'Admin has temporarily suspended coin gifting.' });
                return;
              }
              onGift?.(report.id);
            }}
            className="p-2 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors border border-amber-100/50"
            title="Gift Coins"
          >
            <Zap className="w-4 h-4 fill-amber-500" />
          </button>
          <button 
            onClick={() => {
              toast.info("Reported User", {
                description: `A trust safety report has been filed against ${report.userName}. Our team will review their trust score.`
              });
            }}
            className="text-slate-400 hover:text-red-600 transition-colors"
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="bg-slate-50 text-slate-600 text-xs px-2.5 py-1 rounded-full font-semibold border border-slate-200 flex items-center gap-1.5">
            {TYPE_ICONS[report.type] || <Clock className="w-3.5 h-3.5" />}
            {report.subType || report.type}
          </span>
          <span className="text-sm text-slate-500 flex items-center gap-1 font-medium">
            <MapPin className="w-3.5 h-3.5" />
            {report.location}, {report.region}
          </span>
          {(report as any).isEdited && (
            <span className="bg-blue-50 text-blue-600 text-[10px] px-2 py-0.5 rounded-full font-medium border border-blue-100">
              Edited
            </span>
          )}
        </div>
        <p className="text-slate-900 text-base leading-relaxed font-medium">{report.content || (report as any).description || 'No description provided'}</p>
      </div>

      {/* Metadata / Specific fields */}
      {report.metadata && (report.metadata.price || report.metadata.status) && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {report.metadata.price && (
            <div className="bg-green-50 text-green-700 text-xs px-2.5 py-1 rounded-lg border border-green-100 font-medium">
              Price: {report.metadata.price}
            </div>
          )}
          {report.metadata.status && (
            <div className={`text-xs px-2.5 py-1 rounded-lg border font-medium ${
              report.metadata.status === 'outage' || report.metadata.status === 'delayed' 
                ? 'bg-red-50 text-red-700 border-red-100' 
                : 'bg-blue-50 text-blue-700 border-blue-100'
            }`}>
              Status: {report.metadata.status.toUpperCase()}
            </div>
          )}
        </div>
      )}

      {/* Image if any */}
      {report.imageUrl && (
        <div className="px-4 pb-4">
          <div className="rounded-xl overflow-hidden aspect-[16/9] border border-slate-100">
            <ImageWithFallback src={report.imageUrl} alt="Report visual" className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 border-t border-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => onLike?.(report.id)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-green-600 transition-colors"
          >
            <ThumbsUp className="w-4 h-4" />
            <span className="text-xs font-medium">{report.likes}</span>
          </button>
          <button 
            onClick={() => onComment?.(report.id)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-blue-600 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-xs font-medium">{report.comments?.length || 0}</span>
          </button>
          <button 
            onClick={() => {
              if (isUserPost) {
                toast.info("Can't repost your own post", {
                  description: "Share it with others instead!"
                });
                return;
              }
              if (hasReposted) {
                toast.info("Already reposted", {
                  description: "You've already shared this to your followers"
                });
                return;
              }
              setHasReposted(true);
              onRepost?.(report.id);
            }}
            className={`flex items-center gap-1.5 transition-colors ${
              hasReposted 
                ? 'text-green-600' 
                : 'text-slate-500 hover:text-green-600'
            }`}
            disabled={isUserPost}
            title={isUserPost ? "Can't repost your own post" : "Repost this to amplify important reports"}
          >
            <Repeat2 className={`w-4 h-4 ${hasReposted ? 'fill-green-600' : ''}`} />
            <span className="text-xs font-medium">
              {report.repostCount || 0}
            </span>
          </button>
        </div>
        <button 
          onClick={async () => {
            const contentText = report.content || (report as any).description || 'Sentlo Report';
            if (navigator.share) {
              navigator.share({
                title: `Sentlo Report: ${report.subType || report.type}`,
                text: contentText,
                url: window.location.href,
              }).catch(() => toast.info('Link copied to clipboard!'));
            } else {
              const success = await copyToClipboard(`${contentText}\n\nShared via Sentlo`);
              if (success) toast.success('Link copied to clipboard!');
            }
          }}
          className="flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 transition-colors"
        >
          <Share2 className="w-4 h-4" />
          <span className="text-xs font-medium">Share</span>
        </button>
      </div>
    </motion.div>
    </UserPostBadge>
  );
});

ReportCard.displayName = 'ReportCard';